// Coral Reef skin: a scuba diver swims the reef to a sunken hatch.
//   idle    - diver hovers by the hatch; bubbles rise
//   pending - diver swims the reef; progress eases them toward the hatch
//   failure - diver freezes and a shark bars the way
//   success - the sunken hatch lights green
import { makeScene, mix, type Palette, type SceneCtx } from '../scene/engine';
import type { SkinDef } from './types';

const colors = (g: (n: string) => string): Palette => ({
  waterTop: mix(g('--bg-raise'), g('--accent'), 0.20), waterBot: g('--bg'),
  ground: g('--surface'), groundLo: g('--bg-raise'), edge: g('--line-bright'), dash: g('--line'),
  accent: g('--accent'), green: g('--green'), red: g('--red'), amber: g('--amber'), gild: g('--gild'),
  suit: '#20303a', suitHi: g('--accent'), tank: '#c0c8d0', mask: '#bfefff', skin: '#e9cfa0',
  coral: g('--red'), coral2: g('--violet'), coral3: g('--amber'),
  shark: '#5b6b78', sharkHi: '#7c8c99', sharkBelly: '#c3d0d8', sharkEye: '#0b1418',
  bubble: mix(g('--accent'), '#ffffff', 0.5), sun: mix(g('--accent'), '#ffffff', 0.6),
  metal: '#8a94a0', metalLo: '#4c5560',
});

function sunshafts(s: SceneCtx, C: Palette) {
  const { glow, groundY: GY } = s;
  glow(30, 0, 8, GY, C.sun, 0.06); glow(96, 0, 6, GY, C.sun, 0.05); glow(158, 0, 7, GY, C.sun, 0.06);
}
function bubbles(s: SceneCtx, C: Palette, f: number) {
  const { px, groundY: GY, W } = s;
  for (let i = 0; i < 6; i++) { const x = (i * 39 + 11) % W, y = (GY - 3 - ((i * 13 + f * 2) % (GY - 4))); px(x, y, i % 2 ? 2 : 1, i % 2 ? 2 : 1, C.bubble); }
}
// A chunky reef mound: branching fronds + brain-coral knobs on a base.
function coral(s: SceneCtx, C: Palette, x: number, kind: number) {
  const { px, groundY: GY } = s; const by = GY + 1;
  const c = kind % 3 === 0 ? C.coral : kind % 3 === 1 ? C.coral2 : C.coral3;
  const hi = mix(c, '#ffffff', 0.3), lo = mix(c, C.ground, 0.25);
  px(x - 6, by - 3, 15, 3, lo);                         // base mound
  px(x, by - 13, 2, 13, c); px(x, by - 15, 2, 2, hi);   // tall centre frond
  px(x - 5, by - 9, 2, 9, c); px(x - 5, by - 10, 2, 1, hi);
  px(x + 5, by - 11, 2, 11, c); px(x + 5, by - 13, 2, 2, hi);
  px(x - 2, by - 7, 2, 7, lo); px(x + 2, by - 8, 2, 8, lo);
  px(x - 8, by - 5, 3, 3, C.coral2); px(x + 8, by - 6, 3, 3, C.coral3); // knobs
}
// A little fish; drifts toward the reef, tail trailing left.
function fish(s: SceneCtx, C: Palette, x: number, y: number, color: string) {
  const { px } = s;
  px(x, y, 4, 3, color);                                     // body
  px(x - 2, y, 1, 1, color); px(x - 2, y + 2, 1, 1, color); px(x - 1, y + 1, 1, 1, color); // tail
  px(x + 1, y - 1, 2, 1, mix(color, '#ffffff', 0.35));       // dorsal
  px(x + 3, y + 1, 1, 1, C.sharkEye);                        // eye
}
function hatch(s: SceneCtx, C: Palette, green: boolean) {
  const { px, glow, groundY: GY, W } = s; const bx = W - 34, by = GY;
  if (green) glow(bx - 6, by - 20, 34, 24, C.green, 0.16);
  // sunken bulkhead + round hatch
  px(bx, by - 16, 24, 16, C.metalLo); px(bx, by - 16, 24, 1, C.metal); px(bx, by - 16, 1, 16, C.metal);
  const ring = green ? C.green : C.metal;
  px(bx + 6, by - 13, 12, 12, mix('#04202a', C.metalLo, 0.5));
  px(bx + 6, by - 13, 12, 1, ring); px(bx + 6, by - 2, 12, 1, ring); px(bx + 6, by - 13, 1, 12, ring); px(bx + 17, by - 13, 1, 12, ring);
  px(bx + 11, by - 8, 2, 2, green ? C.green : C.amber);   // wheel handle
  px(bx + 3, by - 19, 2, 4, C.metal);                     // pipe stub
}
function diver(s: SceneCtx, C: Palette, x: number, walkF: number, tint: string, resting: boolean) {
  const { px, groundY: GY } = s;
  const fy = GY + 2, bob = !resting && walkF % 2 ? -1 : 0, y = fy - 15 + bob;
  // fins kicking
  if (resting) { px(x + 2, fy - 3, 4, 2, C.suitHi); px(x + 7, fy - 3, 4, 2, C.suitHi); }
  else { const k = walkF % 2 ? 1 : -1; px(x + 1, fy - 3 - (k > 0 ? 1 : 0), 4, 2, C.suitHi); px(x + 7, fy - 3 + (k > 0 ? 1 : 0), 4, 2, C.suitHi); }
  // wetsuit body
  px(x + 3, y + 6, 7, 8, C.suit); px(x + 3, y + 6, 7, 1, C.suitHi); px(x + 4, y + 9, 5, 1, C.suitHi);
  // air tank on the back
  px(x + 1, y + 6, 2, 6, C.tank); px(x + 1, y + 5, 2, 1, C.metal);
  // head + dive mask
  px(x + 4, y + 1, 6, 5, C.skin); px(x + 4, y + 2, 5, 2, C.mask); px(x + 4, y, 6, 1, C.suit);
  // regulator bubbles from the mouth
  px(x + 10, y + 4, 1, 1, C.bubble);
  // arm / dive light
  if (resting) px(x + 9, y + 8, 2, 3, C.tank); else { px(x + 10, y + 7, 2, 2, C.skin); px(x + 12, y + 6, 1, 1, tint); }
}
function shark(s: SceneCtx, C: Palette, x: number) {
  const { px, groundY: GY } = s; const y = GY - 10;
  px(x + 2, y + 3, 12, 5, C.shark); px(x + 2, y + 7, 11, 2, C.sharkBelly);   // body + belly
  px(x, y + 4, 3, 3, C.shark); px(x - 2, y + 5, 2, 1, C.shark);              // snout
  px(x + 13, y + 1, 3, 4, C.shark); px(x + 14, y, 2, 2, C.sharkHi);          // tail
  px(x + 6, y, 3, 3, C.sharkHi);                                            // dorsal fin
  px(x + 2, y + 4, 1, 1, C.sharkEye);                                        // eye
  for (let i = 0; i < 4; i++) px(x + 1 + i, y + 7, 1, 1, i % 2 ? C.sharkBelly : C.shark); // gills/teeth hint
}

const draw = (C: Palette, s: SceneCtx) => {
  const { px, mix: m, W, H, groundY: GY, pathL: PL, state: st, heroX: hx, walkF, flameF } = s;
  const tint = st === 'failure' ? C.red : st === 'success' ? C.green : C.accent;
  const allDone = st === 'success';
  // water column
  for (let y = 0; y < GY; y++) px(0, y, W, 1, m(C.waterTop, C.waterBot, y / GY));
  sunshafts(s, C);
  bubbles(s, C, walkF + flameF);
  // a small school drifting through the water column
  const fx = walkF * 2;
  fish(s, C, ((22 + fx) % (W + 16)) - 8, 11, C.coral3);
  fish(s, C, ((78 + fx) % (W + 16)) - 8, 20, C.accent);
  fish(s, C, ((116 + fx) % (W + 16)) - 8, 8, C.coral2);
  fish(s, C, ((156 + fx) % (W + 16)) - 8, 17, C.amber);
  // seabed
  for (let y = GY; y < H; y++) px(0, y, W, 1, m(C.ground, C.groundLo, (y - GY) / (H - GY)));
  px(0, GY - 1, W, 2, C.edge);
  // swim trail along the seabed
  const progX = st === 'idle' ? PL : hx + 6;
  for (let x = PL; x < W - 8; x += 10) { const done = allDone || x < progX; px(x, GY + 3, 6, 2, done ? tint : C.dash); }
  px(PL, GY + 2, allDone ? W - 8 - PL : Math.max(0, progX - PL), 1, m(tint, C.ground, 0.3));
  coral(s, C, 30, 0); coral(s, C, 104, 1); coral(s, C, 160, 2);
  hatch(s, C, st === 'success');
  diver(s, C, hx, walkF, tint, st === 'idle');
  if (st === 'failure') shark(s, C, hx + 13);
};

export const reef: SkinDef = {
  label: 'Coral Reef',
  PromoteScene: makeScene({ colors, draw }),
  lines: {
    pending: 'Descending — mind your air.',
    failure: 'Shark on the reef. Hold position.',
    success: 'Hatch is open. Surface! 🫧',
  },
};
